import {
  getDateFromNric,
  getGenderFromNric,
  removeLeadingZero,
} from '@/utils/applicant';
import { formatDisplayDate, formatRequestDate, safeDate } from '@/utils/date';
import { getBase64 } from '@/utils/helper';
import { getPreferredLang } from '@/utils/preferredLang';
import { useProductCode } from '@/utils/product';
import { yupResolver } from '@hookform/resolvers/yup';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePostApplicationMutation } from '../apis';
import {
  AGENT_CODE_INSURANCE,
  AGENT_CODE_TAKAFUL,
  BRANCH_CODE,
  RACE_MALAY,
} from '../constants';
import {
  AddressType,
  Answer,
  ApplicantType,
  ClientType,
  Entity,
  ErrorCode,
  ErrorCodePrefix,
  Gender,
  IdType,
  PaymentMode,
  PaymentSource,
  Relationship,
} from '../enums';
import { useAppDispatch, useAppSelector } from '../reducers';
import {
  resetState,
  updateFormValues,
  updatePremiums,
} from '../reducers/application';
import { useApplicationSchema } from '../schemas';
import {
  ApiResponse,
  ApplicationApplicant,
  ApplicationRequest,
} from '../types/api';
import { IsError } from '../types/component';
import {
  AddressFormValues,
  ApplicationFormValues,
  QuotationFormValues,
} from '../types/form';
import { Address, Applicant } from '../types/model';
import { useOptions } from './useOptions';
import { useRoute } from './useRoute';
import { pushDataLayer } from '@/utils/googleAnalytics';
import { useGAFactory } from './useGAFactory';

export const useApplication = () => {
  const options = useOptions();
  const dispatch = useAppDispatch();
  const schema = useApplicationSchema();
  const application = useAppSelector((state) => state.application);
  const [postApplication, { isLoading }] = usePostApplicationMutation();
  const { i18n } = useTranslation();
  const productCode = useProductCode();
  const { entity, redirect } = useRoute();
  const [isContactDetailsOpen, setIsContactDetailsOpen] = useState(false);
  const [isError, setIsError] = useState<IsError>({
    isErrorAge: false,
    isErrorOccupation: false,
  });
  const GAFactory = useGAFactory();
  const paymentMode = application.paymentMode;
  const amount =
    paymentMode === PaymentMode.Annually
      ? (application.premiums?.yearlyTotalPremium ?? 0)
      : (application.premiums?.monthlyTotalPremium ?? 0);

  useEffect(() => {
    if (!application.formValues?.quotation) {
      redirect('quotation');
    }
  }, [application.formValues?.quotation]);
  const relationship =
    application.formValues.quotation?.life_insured.relationship;
  const formMethods = useForm<ApplicationFormValues>({
    resolver: yupResolver(schema.applicationSchema),
    context: { relationship },
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: application.formValues.application
      ? application.formValues.application
      : application.formValues.quotation?.policy_owner
        ? {
            policy_owner: {
              personalDetail: {
                name: application.formValues.quotation?.policy_owner.name,
                nric: application.formValues.quotation?.policy_owner.nric,
                gender: getGenderFromNric(
                  application.formValues.quotation?.policy_owner.nric ?? '',
                ),
                email: application.formValues.quotation?.policy_owner.email,
                occupation:
                  application.formValues.quotation?.policy_owner.occupation,
                // UAT #4 — display DOB as DD/MM/YYYY (was raw ISO).
                // The API request still uses formatRequestDate(getDateFromNric(…)).
                dob: formatDisplayDate(
                  getDateFromNric(
                    application.formValues.quotation?.policy_owner.nric,
                  ),
                ),
              },
            },
            life_insured: {
              personalDetail: {
                name: application.formValues.quotation?.life_insured.name,
                nric: application.formValues.quotation?.life_insured.nric,
                gender: getGenderFromNric(
                  application.formValues.quotation?.life_insured.nric ?? '',
                ),
                email: application.formValues.quotation?.life_insured.email,
                occupation:
                  application.formValues.quotation?.life_insured.occupation,
                dob: formatDisplayDate(
                  safeDate(
                    getDateFromNric(
                      application.formValues.quotation?.life_insured.nric ?? '',
                    ),
                  ),
                ),
              },
            },
            bankDetail: {
              fullname: application.formValues.quotation?.policy_owner.name,
            },
          }
        : {
            policy_owner: {
              personalDetail: {
                name: application.formValues.quotation?.life_insured.name,
                nric: application.formValues.quotation?.life_insured.nric,
                gender: getGenderFromNric(
                  application.formValues.quotation?.life_insured.nric ?? '',
                ),
                email: application.formValues.quotation?.life_insured.email,
                occupation:
                  application.formValues.quotation?.life_insured.occupation,
                dob: formatDisplayDate(
                  safeDate(
                    getDateFromNric(
                      application.formValues.quotation?.life_insured.nric ?? '',
                    ),
                  ),
                ),
              },
            },
            bankDetail: {
              fullname: application.formValues.quotation?.life_insured.name,
            },
          },
  });

  const onCloseContactDetailsForm = () => {
    setIsContactDetailsOpen(false);
    redirect('quotation');
    dispatch(resetState());
  };

  const getOccupationDesc = (code: string) => {
    return options.occupations
      .flatMap(({ options }) => options)
      .find(({ value }) => value === code)?.label;
  };

  const mapToAddress = (
    formValue: AddressFormValues,
  ): Omit<Address, 'type'> => ({
    addr1: formValue.address1,
    addr2: formValue.address2,
    postcode: formValue.postcode,
    city: formValue.city,
    state: formValue.state,
    country: formValue.country,
  });

  const getRelationship = (formValues: QuotationFormValues): Relationship => {
    // get policy owner relationship
    if (
      formValues.life_insured.gender === Gender.MALE &&
      formValues.life_insured.relationship === Relationship.Spouse
    ) {
      return Relationship.Husband;
    } else if (
      formValues.life_insured.gender === Gender.FEMALE &&
      formValues.life_insured.relationship === Relationship.Spouse
    ) {
      return Relationship.Wife;
    } else {
      return Relationship.Self;
    }
  };

  const getApplicantsInfo = (
    formValues: ApplicationFormValues,
  ): ApplicationApplicant[] => {
    var { life_insured: lifeInsured, policy_owner: policyOwner } = formValues;
    const quotationFormValues = application.formValues.quotation!;
    const result: Applicant[] = [];

    const createApplicant = (
      clientType: ClientType,
      person: {
        gender?: string;
        name?: string;
        email?: string;
        nric?: string;
        relationship?: string;
        occupation?: string;
      },
      applicantType: ApplicantType,
      relationshipOverride?: Relationship,
    ): Applicant =>
      ({
        clientType,
        gender: formValues[applicantType]?.personalDetail.gender,
        name: { fullName: person.name as string },
        emails: [
          {
            type: 'email',
            value: person.email as string,
            isPrimary: Answer.Yes,
          },
        ],
        planCode: application.planCode,
        idType: IdType.IC,
        idNo: person.nric as string,
        relationship: (relationshipOverride ??
          person.relationship) as Relationship,
        dob: formatRequestDate(getDateFromNric(person.nric as string)),
        occupationCode: person.occupation,
        deductibleAmount: application.deductible,
        occupationDesc: getOccupationDesc(person.occupation as string),
        title: formValues[applicantType]?.personalDetail.title,
        pdpaConsent: Answer.No,
        race: formValues[applicantType]?.personalDetail.race as string,
        isBumiputra:
          formValues[applicantType]?.personalDetail.race === RACE_MALAY,
        religion: formValues[applicantType]?.personalDetail.religion as string,
        nationality: formValues[applicantType]?.personalDetail.nationality,
        monthlyIncome:
          formValues[applicantType]?.personalDetail.income !== undefined
            ? { amount: formValues[applicantType]?.personalDetail.income }
            : undefined,
        maritalStatus: formValues[applicantType]?.personalDetail.maritalStatus,
        companyName: formValues[applicantType]?.personalDetail.employer,
        contacts: [
          {
            type: 'Mobile',
            value:
              `+${formValues[applicantType]?.personalDetail.mobilePrefix}` +
              removeLeadingZero(
                formValues[applicantType]?.personalDetail.mobile!,
              ),
            isPrimary: Answer.Yes,
          },
        ],
        addresses: [
          {
            ...(formValues.life_insured?.personalDetail.isSameAddressAsPO
              ? {
                  ...mapToAddress(
                    formValues.policy_owner?.personalDetail.homeAddress!,
                  ),
                  type: AddressType.Residential,
                  isPrimary: formValues[applicantType]?.personalDetail
                    .isSameAddress
                    ? Answer.Yes
                    : Answer.No,
                  isSameForMailing:
                    formValues[applicantType]?.personalDetail.isSameAddress,
                }
              : {
                  ...mapToAddress(
                    formValues[applicantType]?.personalDetail.homeAddress!,
                  ),
                  type: AddressType.Residential,
                  isPrimary: formValues[applicantType]?.personalDetail
                    .isSameAddress
                    ? Answer.Yes
                    : Answer.No,
                  isSameForMailing:
                    formValues[applicantType]?.personalDetail.isSameAddress,
                }),
          },
          ...(formValues[applicantType]?.personalDetail.isSameAddress
            ? []
            : [
                {
                  ...mapToAddress(
                    formValues[applicantType]?.personalDetail.mailingAddress!,
                  ),
                  type: AddressType.Mailing,
                  isPrimary: Answer.Yes,
                },
              ]),
        ],
        uploadedNRIC: {
          frontSideImg: getBase64(
            formValues[applicantType]?.personalDetail.uploadedNric
              .frontSideImg ?? '',
          ),
          backSideImg: getBase64(
            formValues[applicantType]?.personalDetail.uploadedNric
              .backSideImg ?? '',
          ),
        },
        fundSource:
          formValues[applicantType]?.personalDetail.fundSource || undefined,
        wealthSource:
          formValues[applicantType]?.personalDetail.wealthSource || undefined,
        fundSourceOthers:
          formValues[applicantType]?.personalDetail.fundSourceOthers ||
          undefined,
      }) as ApplicationApplicant;

    if (quotationFormValues.life_insured.relationship === Relationship.Self) {
      result.push(
        createApplicant(
          ClientType.ProposerOrLifeAssured,
          quotationFormValues.life_insured,
          ApplicantType.PolicyOwner,
        ),
      );
    } else {
      if (policyOwner) {
        var { personalDetail } = policyOwner;
        personalDetail = {
          ...personalDetail,
          gender: personalDetail.gender as Gender,
        };
        result.push(
          createApplicant(
            ClientType.Proposer,
            personalDetail!,
            ApplicantType.PolicyOwner,
            quotationFormValues.life_insured.relationship !==
              Relationship.Spouse
              ? personalDetail.gender === Gender.MALE
                ? Relationship.Father
                : Relationship.Mother
              : Relationship.Spouse,
          ),
        );
      }
      if (lifeInsured) {
        var { personalDetail } = lifeInsured;
        personalDetail = {
          ...personalDetail,
          gender: personalDetail.gender,
        };
        result.push(
          createApplicant(
            ClientType.LifeAssured,
            quotationFormValues.life_insured,
            ApplicantType.LifeInsured,
            Relationship.Self,
          ),
        );
      }
    }

    return result.map((applicant, index) => ({
      ...applicant,
      id: index + 1,
    })) as ApplicationApplicant[];
  };

  const getApplicationRequestData = (
    formValues: ApplicationFormValues,
  ): ApplicationRequest => ({
    appId: application.appId,
    refId: application.refId ?? '',
    productCode,
    paymentMode: application.paymentMode,
    distributionChannel: application.distributionChannel,
    preferredLang: getPreferredLang(i18n.language),
    payMethod: PaymentSource.CreditCard,
    agent: {
      agentCode:
        application.agent?.agentCode ??
        (entity === Entity.INSURANCE
          ? AGENT_CODE_INSURANCE
          : AGENT_CODE_TAKAFUL),
      agentBranch: application.agent?.agentBranch ?? BRANCH_CODE,
    },
    consentConfirmation: false,
    purposeOfTrx: {
      trxCode: formValues.policy_owner?.personalDetail.purposeOfTransaction,
    },
    applicants: getApplicantsInfo(formValues),
  });

  const onSubmit = async (formValues: ApplicationFormValues) => {
    const GAData = GAFactory('begin_checkout', {
      planCode: application.planCode,
      paymentModeFormValue: application.paymentMode,
      deductible: application.deductible,
      applicationValues: formValues,
    });
    pushDataLayer(GAData);
    dispatch(
      updateFormValues({
        application: formValues,
      }),
    );
    try {
      const result = await postApplication({
        body: getApplicationRequestData(formValues),
      }).unwrap();
      dispatch(
        updatePremiums({
          monthlyTotalPremium: result.data?.monthlyTotalPremium,
          yearlyTotalPremium: result.data?.yearlyTotalPremium,
          annualTotalPremium: result.data?.annualTotalPremium,
        }),
      );

      redirect('review');
    } catch (error) {
      const errorResponse = error as FetchBaseQueryError;
      const data = errorResponse.data as ApiResponse<unknown>;

      const isValidationError = [
        ErrorCodePrefix.LVE,
        ErrorCodePrefix.IVE,
        ErrorCodePrefix.DVE,
      ].some((err) => err === data?.errorCode?.substring(0, 3));

      setIsContactDetailsOpen(true);

      if (!isValidationError) {
        redirect('error');
        return;
      }

      const isHitErrorAge = [ErrorCode.ErrorAgeMin, ErrorCode.ErrorAgeMax].some(
        (err) => err === data.errorCode,
      );

      const isDangerousOccupation =
        data.errorCode === ErrorCode.ErrorDangerousOccupation;

      setIsError({
        isErrorAge: isHitErrorAge,
        isErrorOccupation: isDangerousOccupation || !isHitErrorAge,
      });
    }
  };

  const onBack = () => {
    redirect('questionnaire');
  };

  const applicantType = (): ApplicantType | undefined => {
    if (application.formValues.quotation?.life_insured) {
      if (
        application.formValues.quotation?.life_insured.relationship ===
        Relationship.Self
      ) {
        return ApplicantType.PolicyOwner;
      } else return ApplicantType.LifeInsured;
    } else if (application.formValues.quotation?.policy_owner) {
      return ApplicantType.PolicyOwner;
    } else {
      return ApplicantType.LifeInsured;
    }
  }; // check if object exist

  const isKid = (applicant: ApplicantType): boolean => {
    const relationship = application.formValues.quotation?.life_insured
      ?.relationship as Relationship | undefined;
    return (
      applicant === ApplicantType.LifeInsured &&
      (relationship === Relationship.Son ||
        relationship === Relationship.Daughter)
    );
  };

  const isSpouse = (): boolean => {
    const relationship = application.formValues.quotation?.life_insured
      ?.relationship as Relationship | undefined;
    return relationship === Relationship.Spouse;
  };

  const POName = application.formValues.quotation?.policy_owner
    ? application.formValues.quotation?.policy_owner.name
    : application.formValues.quotation?.life_insured?.name;

  return {
    amount,
    paymentMode,
    formMethods,
    isLoading,
    getApplicationRequestData,
    onSubmit,
    onBack,
    isContactDetailsOpen,
    isError,
    onCloseContactDetailsForm,
    applicantType,
    POName,
    isKid,
    isSpouse,
    relationship,
  };
};
